import React, { useEffect, useState } from 'react'

import { useHistory, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
    Button,
    FormControlLabel,
    Switch,
    Tab,
    Tabs,
    TextField,
    Theme,
    Tooltip,
    createStyles,
    makeStyles,
    useTheme,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Radio,
    RadioGroup,
    FormLabel,
} from '@material-ui/core'
import { InfoOutlined } from '@material-ui/icons'

import { Container, Selector } from '../../../../components'
import { axiosInstance } from '../../../../helpers'
import { setWorkSpaceUser } from '../../../../store'
import LoopIcon from '@material-ui/icons/Loop'
import { InHouseVendorHours } from './InHouseVendorHours'
import { useAppDispatch, useCompany, useUser } from '../../../../hooks'
import { useMoveOutRules } from '../../../../hooks/useMoveOutRules'
import { LeaseSyncSchedule } from '../../../../models/Company/types'
import { TOP_BAR_HEIGHT } from '../../../../navigation/constants'
import { CommunitySettings } from './CommunitySettings'
import { LeaseTermsSection } from './LeaseTermsSection'
import { SchoolsServedSettings } from './SchoolsServedSettings'
import {
    parsePropertyDetailsTabIndex,
    propertyDetailsPath,
    propertyDetailsTabSlugFromIndex,
} from './propertyDetailsTabUtils'

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        rotateIcon: {
            animation: 'spin 01s linear infinite',
        },
        indicator: {
            backgroundColor: theme.palette.primary.dark,
        },
        page: {
            display: 'flex',
            flexDirection: 'column',
            // Fixed top bar plus AuthLayout's 24px main padding on top and bottom.
            height: `calc(100vh - ${TOP_BAR_HEIGHT + 48}px)`,
            minHeight: 0,
        },
        tabPanel: {
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
        },
        settingsGroup: {
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            marginTop: theme.spacing(2),
        },
        groupTitle: {
            fontWeight: 700,
            fontSize: '20px',
            color: '#2F2E41',
        },
        cardGrid: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(2),
            alignItems: 'flex-start',
        },
        settingCard: {
            width: 400,
            minWidth: 400,
            maxWidth: 400,
            flex: '0 0 400px',
        },
    }),
)

export const PropertyDetails = () => {
    const classes = useStyles()
    const history = useHistory()
    const location = useLocation()
    const { workspaceUser, userList, actions } = useUser()
    const { getUserList } = actions
    const theme = useTheme()

    const dispatch = useAppDispatch()
    const selectedTab = parsePropertyDetailsTabIndex(location.search)

    const [loading, setLoading] = useState(false)

    const [allowServiceRequest, setAllowServiceRequest] = useState(
        workspaceUser?.active_workspace.allow_service_request ? true : false,
    )

    const [autoMoveInEnabled, setAutoMoveInEnabled] = useState(
        workspaceUser?.active_workspace.auto_move_in_enabled ?? false,
    )
    const [autoMoveInDaysToComplete, setAutoMoveInDaysToComplete] = useState(
        workspaceUser?.active_workspace.auto_move_in_days_to_complete?.toString() ??
            '',
    )
    const [sendDateOffset, setSendDateOffset] = useState(
        workspaceUser?.active_workspace.send_date_offset?.toString() ?? '',
    )

    const [runAutoMoveOutEvents, setRunAutoMoveOutEvents] = useState(
        workspaceUser?.active_workspace.run_auto_move_out_events ?? false,
    )
    const [daysBeforeMoveOutSchedule, setDaysBeforeMoveOutSchedule] = useState(
        workspaceUser?.active_workspace.days_before_move_out_schedule?.toString() ??
            '',
    )
    const [defaultMoveOutRule, setDefaultMoveOutRule] = useState<number | ''>(
        workspaceUser?.active_workspace.default_move_out_rule ?? '',
    )

    const { moveOutRuleList, getMoveOutRuleList } = useMoveOutRules()

    const [
        leaseSyncSchedule,
        setLeaseSyncSchedule,
    ] = useState<LeaseSyncSchedule | null>(
        workspaceUser?.active_workspace.lease_sync_schedule ?? null,
    )

    const { vendorList, setVendorList } = useCompany({
        getVendorList: true,
        cleanUp: true,
    })

    const [backupUserOne, setBackupUserOne] = useState(
        workspaceUser?.active_workspace.on_call_backups?.backup_user_one?.id ??
            -1,
    )
    const [backupUserTwo, setBackupUserTwo] = useState(
        workspaceUser?.active_workspace.on_call_backups?.backup_user_two?.id ??
            -1,
    )
    const [backupUserThree, setBackupUserThree] = useState(
        workspaceUser?.active_workspace.on_call_backups?.backup_user_three
            ?.id ?? -1,
    )

    const [tenantSyncedDamageView, setTenantSyncedDamageView] = useState(
        workspaceUser?.active_workspace.tenant_synced_damage_view ?? false,
    )
    const [tenantApprovedDamageView, setTenantApprovedDamageView] = useState(
        workspaceUser?.active_workspace.tenant_approved_damage_view ?? false,
    )

    const [leaseDamageEndDateCutoff, setLeaseDamageEndDateCutoff] = useState(
        workspaceUser?.active_workspace.lease_damage_end_date_cutoff?.toString() ??
            '',
    )
    const [scheduleAutoArchiveDays, setScheduleAutoArchiveDays] = useState(
        workspaceUser?.active_workspace.schedule_auto_archive_days?.toString() ??
            '',
    )

    const [aiProjectReportsEnabled, setAiProjectReportsEnabled] = useState(
        workspaceUser?.active_workspace.ai_project_reports_enabled ?? false,
    )

    const [doValidate, setDoValidate] = useState(false)

    useEffect(() => {
        getUserList({ params: { my_team: true } })
    }, [])

    useEffect(() => {
        getMoveOutRuleList()
    }, [])

    const onSave = () => {
        const body = new FormData()

        if (backupUserOne !== -1) {
            const onCallBackReq = new FormData()
            onCallBackReq.append('backup_user_one', backupUserOne.toString())

            if (backupUserTwo !== -1) {
                onCallBackReq.append(
                    'backup_user_two',
                    backupUserTwo.toString(),
                )
            }

            if (backupUserThree !== -1) {
                onCallBackReq.append(
                    'backup_user_three',
                    backupUserThree.toString(),
                )
            }

            const onCallBackReqString = JSON.stringify(
                Object.fromEntries(onCallBackReq.entries()),
            )
            body.append('on_call_backups', onCallBackReqString)
        }

        body.append(
            'allow_service_request',
            allowServiceRequest ? 'True' : 'False',
        )

        body.append(
            'auto_move_in_enabled',
            autoMoveInEnabled ? 'True' : 'False',
        )

        if (autoMoveInDaysToComplete) {
            body.append(
                'auto_move_in_days_to_complete',
                autoMoveInDaysToComplete,
            )
        }

        if (sendDateOffset) {
            body.append('send_date_offset', sendDateOffset)
        }

        if (leaseSyncSchedule) {
            body.append('lease_sync_schedule', leaseSyncSchedule.toString())
        }

        body.append(
            'run_auto_move_out_events',
            runAutoMoveOutEvents ? 'True' : 'False',
        )

        if (daysBeforeMoveOutSchedule) {
            body.append(
                'days_before_move_out_schedule',
                daysBeforeMoveOutSchedule,
            )
        }

        if (defaultMoveOutRule !== '') {
            body.append('default_move_out_rule', defaultMoveOutRule.toString())
        }

        body.append(
            'tenant_synced_damage_view',
            tenantSyncedDamageView ? 'True' : 'False',
        )

        body.append(
            'tenant_approved_damage_view',
            tenantApprovedDamageView ? 'True' : 'False',
        )

        if (leaseDamageEndDateCutoff) {
            body.append(
                'lease_damage_end_date_cutoff',
                leaseDamageEndDateCutoff,
            )
        }

        body.append('schedule_auto_archive_days', scheduleAutoArchiveDays)

        body.append(
            'ai_project_reports_enabled',
            aiProjectReportsEnabled ? 'True' : 'False',
        )

        setLoading(true)
        axiosInstance
            .patch(
                `company/apartment/${workspaceUser?.active_workspace.id}/`,
                body,
            )
            .then((res) => {
                const updatedUser = workspaceUser
                if (updatedUser?.active_workspace) {
                    updatedUser.active_workspace = res.data
                    dispatch(setWorkSpaceUser(updatedUser))
                }
                toast.success(`${workspaceUser?.active_workspace.name} updated`)
            })
            .catch((e) => {
                toast.error(e.response.data.message)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const checkValid = () => {
        if (
            backupUserOne === -1 &&
            (backupUserTwo !== -1 || backupUserThree !== -1)
        ) {
            setDoValidate(true)
            return false
        } else if (backupUserTwo === -1 && backupUserThree !== -1) {
            setDoValidate(true)
            return false
        } else if (scheduleAutoArchiveDays === '') {
            setDoValidate(true)
            toast.error('Schedule auto archive days is required')
            return false
        }
        return true
    }

    const tabStyle: React.CSSProperties = {
        fontWeight: 800,
        fontSize: '15px',
        textTransform: 'none',
    }

    return (
        <div className={classes.page}>
            <Container style={{ flexShrink: 0 }}>
                <Tabs
                    style={{
                        color: 'black',
                        borderBottom: `1px solid ${theme.palette.lightGrey.main}`,
                        flex: 1,
                        textTransform: 'none',
                    }}
                    value={selectedTab}
                    onChange={(_, v) => {
                        if (v === selectedTab) return
                        history.replace(
                            propertyDetailsPath(
                                propertyDetailsTabSlugFromIndex(v),
                            ),
                        )
                    }}
                    classes={{
                        indicator: classes.indicator,
                    }}
                >
                    <Tab label="Details" style={{ ...tabStyle }} />
                    <Tab label="Community" style={{ ...tabStyle }} />
                    <Tab label="Vendor Hours" style={{ ...tabStyle }} />
                    <Tab label="Schools Served" style={{ ...tabStyle }} />
                    <Tab label="Lease Terms" style={{ ...tabStyle }} />
                </Tabs>
            </Container>
            <div className={classes.tabPanel}>
                {selectedTab === DETAILS_TAB && (
                    <Container
                        style={{
                            flex: 1,
                            flexDirection: 'column',
                            padding: theme.spacing(1),
                        }}
                    >
                        <Container direction="column">
                            <div className={classes.settingsGroup}>
                                <span className={classes.groupTitle}>
                                    Move In and Move Out
                                </span>
                                <div className={classes.cardGrid}>
                                    {workspaceUser?.active_workspace
                                        .entrata_integrated && (
                                        <Container
                                            className={classes.settingCard}
                                            style={{
                                                flexDirection: 'column',
                                                padding: theme.spacing(2),
                                                border: `1px solid ${theme.palette.grey[300]}`,
                                                borderRadius: theme.spacing(1),
                                                backgroundColor:
                                                    theme.palette.grey[50],
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontWeight: 600,
                                                    fontSize: '18px',
                                                    marginBottom: theme.spacing(
                                                        2,
                                                    ),
                                                    color:
                                                        theme.palette.primary
                                                            .main,
                                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                    paddingBottom: theme.spacing(
                                                        0.5,
                                                    ),
                                                    display: 'inline-block',
                                                    alignSelf: 'flex-start',
                                                    width: 'fit-content',
                                                }}
                                            >
                                                Auto Sync Lease Schedule
                                            </span>
                                            <FormControl
                                                variant="outlined"
                                                style={{
                                                    width: '100%',
                                                    maxWidth: '400px',
                                                }}
                                                size="small"
                                            >
                                                <InputLabel>
                                                    Auto Sync Lease Schedule
                                                </InputLabel>
                                                <Select
                                                    value={
                                                        leaseSyncSchedule ||
                                                        LeaseSyncSchedule.NONE
                                                    }
                                                    onChange={(e) =>
                                                        setLeaseSyncSchedule(
                                                            e.target
                                                                .value as LeaseSyncSchedule,
                                                        )
                                                    }
                                                    label="Auto Sync Lease Schedule"
                                                >
                                                    <MenuItem
                                                        value={
                                                            LeaseSyncSchedule.NONE
                                                        }
                                                    >
                                                        None
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={
                                                            LeaseSyncSchedule.WEEKLY
                                                        }
                                                    >
                                                        Weekly
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={
                                                            LeaseSyncSchedule.MONTHLY
                                                        }
                                                    >
                                                        Monthly
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Container>
                                    )}

                                    <Container
                                        className={classes.settingCard}
                                        style={{
                                            flexDirection: 'column',
                                            padding: theme.spacing(2),
                                            border: `1px solid ${theme.palette.grey[300]}`,
                                            borderRadius: theme.spacing(1),
                                            backgroundColor:
                                                theme.palette.grey[50],
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontWeight: 600,
                                                fontSize: '18px',
                                                marginBottom: theme.spacing(2),
                                                color:
                                                    theme.palette.primary.main,
                                                borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                paddingBottom: theme.spacing(
                                                    0.5,
                                                ),
                                                display: 'inline-block',
                                                alignSelf: 'flex-start',
                                                width: 'fit-content',
                                            }}
                                        >
                                            Auto Move-In Settings
                                        </span>

                                        <Container
                                            style={{
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                marginBottom: theme.spacing(2),
                                                padding: theme.spacing(1.5),
                                                backgroundColor: 'white',
                                                borderRadius: theme.spacing(
                                                    0.5,
                                                ),
                                                border: `1px solid ${theme.palette.grey[200]}`,
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={
                                                            autoMoveInEnabled
                                                        }
                                                        color="primary"
                                                        onChange={(e) => {
                                                            setAutoMoveInEnabled(
                                                                !autoMoveInEnabled,
                                                            )
                                                        }}
                                                    />
                                                }
                                                labelPlacement="end"
                                                label={
                                                    <span
                                                        style={{
                                                            fontWeight: 500,
                                                            fontSize: '16px',
                                                            color: autoMoveInEnabled
                                                                ? theme.palette
                                                                      .primary
                                                                      .main
                                                                : theme.palette
                                                                      .text
                                                                      .secondary,
                                                        }}
                                                    >
                                                        Enable Auto Move-In
                                                    </span>
                                                }
                                                style={{ margin: 0 }}
                                            />
                                        </Container>

                                        <Container
                                            style={{
                                                flexDirection: 'column',
                                                gap: theme.spacing(2),
                                            }}
                                        >
                                            <TextField
                                                variant="outlined"
                                                label="Days to Complete Move-In"
                                                value={autoMoveInDaysToComplete}
                                                onChange={(e) =>
                                                    setAutoMoveInDaysToComplete(
                                                        e.target.value,
                                                    )
                                                }
                                                style={{
                                                    width: '100%',
                                                    maxWidth: '400px',
                                                }}
                                                size="small"
                                                type="number"
                                                disabled={!autoMoveInEnabled}
                                                helperText={
                                                    !autoMoveInEnabled
                                                        ? 'Enable auto move-in to configure this setting'
                                                        : 'Number of days to complete the move-in process'
                                                }
                                                inputProps={{
                                                    min: 0,
                                                    step: 1,
                                                }}
                                                InputProps={{
                                                    style: {
                                                        backgroundColor: autoMoveInEnabled
                                                            ? 'white'
                                                            : theme.palette
                                                                  .grey[100],
                                                    },
                                                }}
                                            />

                                            <TextField
                                                variant="outlined"
                                                label="Send Date Offset (Days before move-in)"
                                                value={sendDateOffset}
                                                onChange={(e) =>
                                                    setSendDateOffset(
                                                        e.target.value,
                                                    )
                                                }
                                                style={{
                                                    width: '100%',
                                                    maxWidth: '400px',
                                                }}
                                                size="small"
                                                type="number"
                                                disabled={!autoMoveInEnabled}
                                                helperText={
                                                    !autoMoveInEnabled
                                                        ? 'Enable auto move-in to configure this setting'
                                                        : 'Number of days before move-in to send notifications'
                                                }
                                                inputProps={{
                                                    min: 0,
                                                    step: 1,
                                                }}
                                                InputProps={{
                                                    style: {
                                                        backgroundColor: autoMoveInEnabled
                                                            ? 'white'
                                                            : theme.palette
                                                                  .grey[100],
                                                    },
                                                }}
                                            />
                                        </Container>
                                    </Container>

                                    <Container
                                        className={classes.settingCard}
                                        style={{
                                            flexDirection: 'column',
                                            padding: theme.spacing(2),
                                            border: `1px solid ${theme.palette.grey[300]}`,
                                            borderRadius: theme.spacing(1),
                                            backgroundColor:
                                                theme.palette.grey[50],
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontWeight: 600,
                                                fontSize: '18px',
                                                marginBottom: theme.spacing(2),
                                                color:
                                                    theme.palette.primary.main,
                                                borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                paddingBottom: theme.spacing(
                                                    0.5,
                                                ),
                                                display: 'inline-block',
                                                alignSelf: 'flex-start',
                                                width: 'fit-content',
                                            }}
                                        >
                                            Auto Move Out Settings
                                        </span>

                                        <Container
                                            style={{
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                marginBottom: theme.spacing(2),
                                                padding: theme.spacing(1.5),
                                                backgroundColor: 'white',
                                                borderRadius: theme.spacing(
                                                    0.5,
                                                ),
                                                border: `1px solid ${theme.palette.grey[200]}`,
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={
                                                            runAutoMoveOutEvents
                                                        }
                                                        color="primary"
                                                        onChange={(e) => {
                                                            setRunAutoMoveOutEvents(
                                                                !runAutoMoveOutEvents,
                                                            )
                                                        }}
                                                    />
                                                }
                                                labelPlacement="end"
                                                label={
                                                    <span
                                                        style={{
                                                            fontWeight: 500,
                                                            fontSize: '16px',
                                                            color: runAutoMoveOutEvents
                                                                ? theme.palette
                                                                      .primary
                                                                      .main
                                                                : theme.palette
                                                                      .text
                                                                      .secondary,
                                                        }}
                                                    >
                                                        Enable Auto Move-Out
                                                    </span>
                                                }
                                                style={{ margin: 0 }}
                                            />
                                        </Container>

                                        <Container
                                            style={{
                                                flexDirection: 'column',
                                                gap: theme.spacing(2),
                                                marginBottom: theme.spacing(2),
                                            }}
                                        >
                                            <TextField
                                                variant="outlined"
                                                label="Days Before Move Out Schedule"
                                                value={
                                                    daysBeforeMoveOutSchedule
                                                }
                                                onChange={(e) =>
                                                    setDaysBeforeMoveOutSchedule(
                                                        e.target.value,
                                                    )
                                                }
                                                style={{
                                                    width: '100%',
                                                    maxWidth: '400px',
                                                }}
                                                size="small"
                                                type="number"
                                                disabled={!runAutoMoveOutEvents}
                                                helperText={
                                                    !runAutoMoveOutEvents
                                                        ? 'Enable auto move-out to configure this setting'
                                                        : 'Number of days before move out to schedule'
                                                }
                                                inputProps={{
                                                    min: 0,
                                                    step: 1,
                                                }}
                                                InputProps={{
                                                    style: {
                                                        backgroundColor: runAutoMoveOutEvents
                                                            ? 'white'
                                                            : theme.palette
                                                                  .grey[100],
                                                    },
                                                }}
                                            />
                                        </Container>

                                        <FormControl
                                            variant="outlined"
                                            size="small"
                                            style={{
                                                width: '100%',
                                                maxWidth: '400px',
                                            }}
                                            disabled={!runAutoMoveOutEvents}
                                        >
                                            <InputLabel>
                                                Default Move Out Rule
                                            </InputLabel>
                                            <Select
                                                value={
                                                    defaultMoveOutRule === ''
                                                        ? ''
                                                        : defaultMoveOutRule
                                                }
                                                onChange={(e) => {
                                                    const v = e.target.value
                                                    setDefaultMoveOutRule(
                                                        v === ''
                                                            ? ''
                                                            : (v as number),
                                                    )
                                                }}
                                                label="Default Move Out Rule"
                                            >
                                                <MenuItem value="">
                                                    <em>None</em>
                                                </MenuItem>
                                                {moveOutRuleList.map((rule) => (
                                                    <MenuItem
                                                        key={rule.id}
                                                        value={rule.id}
                                                    >
                                                        {rule.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Container>
                                </div>
                            </div>

                            <div className={classes.settingsGroup}>
                                <span className={classes.groupTitle}>
                                    Damages
                                </span>
                                <div className={classes.cardGrid}>
                                    <Container
                                        className={classes.settingCard}
                                        style={{
                                            flexDirection: 'column',
                                            padding: theme.spacing(2),
                                            border: `1px solid ${theme.palette.grey[300]}`,
                                            borderRadius: theme.spacing(1),
                                            backgroundColor:
                                                theme.palette.grey[50],
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontWeight: 600,
                                                fontSize: '18px',
                                                marginBottom: theme.spacing(2),
                                                color:
                                                    theme.palette.primary.main,
                                                borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                paddingBottom: theme.spacing(
                                                    0.5,
                                                ),
                                                display: 'inline-block',
                                                alignSelf: 'flex-start',
                                                width: 'fit-content',
                                            }}
                                        >
                                            Tenant Damage View Settings
                                        </span>
                                        <Container
                                            style={{
                                                flexDirection: 'column',
                                                gap: theme.spacing(2),
                                                padding: theme.spacing(1.5),
                                                backgroundColor: 'white',
                                                borderRadius: theme.spacing(
                                                    0.5,
                                                ),
                                                border: `1px solid ${theme.palette.grey[200]}`,
                                            }}
                                        >
                                            <FormControl component="fieldset">
                                                <FormLabel
                                                    component="legend"
                                                    style={{
                                                        fontWeight: 500,
                                                        fontSize: '16px',
                                                        color:
                                                            theme.palette.text
                                                                .primary,
                                                        marginBottom: theme.spacing(
                                                            1,
                                                        ),
                                                    }}
                                                >
                                                    Can tenants see Synced
                                                    damages?
                                                </FormLabel>
                                                <RadioGroup
                                                    row
                                                    value={
                                                        tenantSyncedDamageView
                                                            ? 'yes'
                                                            : 'no'
                                                    }
                                                    onChange={(e) => {
                                                        setTenantSyncedDamageView(
                                                            e.target.value ===
                                                                'yes',
                                                        )
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        value="yes"
                                                        control={
                                                            <Radio color="primary" />
                                                        }
                                                        label="Yes"
                                                    />
                                                    <FormControlLabel
                                                        value="no"
                                                        control={
                                                            <Radio color="primary" />
                                                        }
                                                        label="No"
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                            <FormControl component="fieldset">
                                                <FormLabel
                                                    component="legend"
                                                    style={{
                                                        fontWeight: 500,
                                                        fontSize: '16px',
                                                        color:
                                                            theme.palette.text
                                                                .primary,
                                                        marginBottom: theme.spacing(
                                                            1,
                                                        ),
                                                    }}
                                                >
                                                    Can tenants see Approved
                                                    damages?
                                                </FormLabel>
                                                <RadioGroup
                                                    row
                                                    value={
                                                        tenantApprovedDamageView
                                                            ? 'yes'
                                                            : 'no'
                                                    }
                                                    onChange={(e) => {
                                                        setTenantApprovedDamageView(
                                                            e.target.value ===
                                                                'yes',
                                                        )
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        value="yes"
                                                        control={
                                                            <Radio color="primary" />
                                                        }
                                                        label="Yes"
                                                    />
                                                    <FormControlLabel
                                                        value="no"
                                                        control={
                                                            <Radio color="primary" />
                                                        }
                                                        label="No"
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                        </Container>
                                    </Container>
                                    <Container
                                        className={classes.settingCard}
                                        style={{
                                            flexDirection: 'column',
                                            padding: theme.spacing(2),
                                            border: `1px solid ${theme.palette.grey[300]}`,
                                            borderRadius: theme.spacing(1),
                                            backgroundColor:
                                                theme.palette.grey[50],
                                        }}
                                    >
                                        <Container
                                            style={{
                                                alignItems: 'center',
                                                alignSelf: 'flex-start',
                                                width: 'fit-content',
                                                marginBottom: theme.spacing(2),
                                                gap: theme.spacing(1),
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontWeight: 600,
                                                    fontSize: '18px',
                                                    color:
                                                        theme.palette.primary
                                                            .main,
                                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                    paddingBottom: theme.spacing(
                                                        0.5,
                                                    ),
                                                    display: 'inline-block',
                                                    width: 'fit-content',
                                                }}
                                            >
                                                Lease Damage Cutoff Settings
                                            </span>
                                            <Tooltip
                                                title="This setting controls how many days after a tenant's lease end date they can still be shown as an option when creating a damage on the mobile app. For example, if set to 30, tenants whose lease ended within the last 30 days will still appear in the tenant selection list."
                                                arrow
                                                placement="right"
                                            >
                                                <InfoOutlined
                                                    style={{
                                                        fontSize: '20px',
                                                        color:
                                                            theme.palette
                                                                .grey[500],
                                                        cursor: 'help',
                                                    }}
                                                />
                                            </Tooltip>
                                        </Container>
                                        <TextField
                                            variant="outlined"
                                            label="Days After Lease End Date"
                                            value={leaseDamageEndDateCutoff}
                                            onChange={(e) =>
                                                setLeaseDamageEndDateCutoff(
                                                    e.target.value,
                                                )
                                            }
                                            style={{
                                                width: '100%',
                                                maxWidth: '400px',
                                            }}
                                            size="small"
                                            type="number"
                                            helperText="Number of days after lease end date to show tenant when creating a damage on the mobile app"
                                            inputProps={{
                                                min: 0,
                                                step: 1,
                                            }}
                                        />
                                    </Container>
                                </div>
                            </div>

                            <div className={classes.settingsGroup}>
                                <span className={classes.groupTitle}>
                                    Archive and Reports
                                </span>
                                <div className={classes.cardGrid}>
                                    <Container
                                        className={classes.settingCard}
                                        style={{
                                            flexDirection: 'column',
                                            padding: theme.spacing(2),
                                            border: `1px solid ${theme.palette.grey[300]}`,
                                            borderRadius: theme.spacing(1),
                                            backgroundColor:
                                                theme.palette.grey[50],
                                        }}
                                    >
                                        <Container
                                            style={{
                                                alignItems: 'center',
                                                alignSelf: 'flex-start',
                                                width: 'fit-content',
                                                marginBottom: theme.spacing(2),
                                                gap: theme.spacing(1),
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontWeight: 600,
                                                    fontSize: '18px',
                                                    color:
                                                        theme.palette.primary
                                                            .main,
                                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                    paddingBottom: theme.spacing(
                                                        0.5,
                                                    ),
                                                    display: 'inline-block',
                                                    width: 'fit-content',
                                                }}
                                            >
                                                Schedule Auto Archive Settings
                                            </span>
                                            <Tooltip
                                                title="Projects that ended more than this many days ago will be automatically archived."
                                                arrow
                                                placement="right"
                                            >
                                                <InfoOutlined
                                                    style={{
                                                        fontSize: '20px',
                                                        color:
                                                            theme.palette
                                                                .grey[500],
                                                        cursor: 'help',
                                                    }}
                                                />
                                            </Tooltip>
                                        </Container>
                                        <TextField
                                            variant="outlined"
                                            label="Days After Completion"
                                            value={scheduleAutoArchiveDays}
                                            onChange={(e) =>
                                                setScheduleAutoArchiveDays(
                                                    e.target.value,
                                                )
                                            }
                                            style={{
                                                width: '100%',
                                                maxWidth: '400px',
                                            }}
                                            size="small"
                                            type="number"
                                            required
                                            error={
                                                doValidate &&
                                                scheduleAutoArchiveDays === ''
                                            }
                                            helperText={
                                                doValidate &&
                                                scheduleAutoArchiveDays === ''
                                                    ? 'Required'
                                                    : 'Number of days after a project ends before it is automatically archived'
                                            }
                                            inputProps={{
                                                min: 0,
                                                step: 1,
                                            }}
                                        />
                                    </Container>
                                    <Container
                                        className={classes.settingCard}
                                        style={{
                                            flexDirection: 'column',
                                            padding: theme.spacing(2),
                                            border: `1px solid ${theme.palette.grey[300]}`,
                                            borderRadius: theme.spacing(1),
                                            backgroundColor:
                                                theme.palette.grey[50],
                                        }}
                                    >
                                        <Container
                                            style={{
                                                alignItems: 'center',
                                                alignSelf: 'flex-start',
                                                width: 'fit-content',
                                                marginBottom: theme.spacing(2),
                                                gap: theme.spacing(1),
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontWeight: 600,
                                                    fontSize: '18px',
                                                    color:
                                                        theme.palette.primary
                                                            .main,
                                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                    paddingBottom: theme.spacing(
                                                        0.5,
                                                    ),
                                                    display: 'inline-block',
                                                    width: 'fit-content',
                                                }}
                                            >
                                                Project Report Settings
                                            </span>
                                            <Tooltip
                                                title="When enabled, this property will receive AI generated reports for their projects. When disabled, they will receive regular reports."
                                                arrow
                                                placement="right"
                                            >
                                                <InfoOutlined
                                                    style={{
                                                        fontSize: '20px',
                                                        color:
                                                            theme.palette
                                                                .grey[500],
                                                        cursor: 'help',
                                                    }}
                                                />
                                            </Tooltip>
                                        </Container>
                                        <Container
                                            style={{
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                padding: theme.spacing(1.5),
                                                backgroundColor: 'white',
                                                borderRadius: theme.spacing(
                                                    0.5,
                                                ),
                                                border: `1px solid ${theme.palette.grey[200]}`,
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={
                                                            aiProjectReportsEnabled
                                                        }
                                                        color="primary"
                                                        onChange={() => {
                                                            setAiProjectReportsEnabled(
                                                                !aiProjectReportsEnabled,
                                                            )
                                                        }}
                                                    />
                                                }
                                                labelPlacement="end"
                                                label={
                                                    <span
                                                        style={{
                                                            fontWeight: 500,
                                                            fontSize: '16px',
                                                            color: aiProjectReportsEnabled
                                                                ? theme.palette
                                                                      .primary
                                                                      .main
                                                                : theme.palette
                                                                      .text
                                                                      .secondary,
                                                        }}
                                                    >
                                                        Enable AI Generated
                                                        Project Reports
                                                    </span>
                                                }
                                                style={{ margin: 0 }}
                                            />
                                        </Container>
                                    </Container>
                                </div>
                            </div>

                            <div className={classes.settingsGroup}>
                                <span className={classes.groupTitle}>
                                    Service Requests
                                </span>
                                <div className={classes.cardGrid}>
                                    <Container
                                        className={classes.settingCard}
                                        style={{
                                            flexDirection: 'column',
                                            padding: theme.spacing(2),
                                            border: `1px solid ${theme.palette.grey[300]}`,
                                            borderRadius: theme.spacing(1),
                                            backgroundColor:
                                                theme.palette.grey[50],
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontWeight: 600,
                                                fontSize: '18px',
                                                marginBottom: theme.spacing(2),
                                                color:
                                                    theme.palette.primary.main,
                                                borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                paddingBottom: theme.spacing(
                                                    0.5,
                                                ),
                                                display: 'inline-block',
                                                alignSelf: 'flex-start',
                                                width: 'fit-content',
                                            }}
                                        >
                                            On Call Backup Configuration
                                        </span>
                                        <Container
                                            style={{
                                                flexDirection: 'column',
                                                gap: theme.spacing(2),
                                            }}
                                        >
                                            <Selector
                                                label="On Call Backup One"
                                                data={userList.filter(
                                                    (user) =>
                                                        user.id !==
                                                            backupUserTwo &&
                                                        user.id !==
                                                            backupUserThree,
                                                )}
                                                searchable
                                                currentValue={backupUserOne}
                                                onChange={(e) => {
                                                    setBackupUserOne(
                                                        e.target
                                                            .value as number,
                                                    )
                                                }}
                                                getDisplayString={(user) =>
                                                    user.name
                                                }
                                                customStyle={{
                                                    formControl: {
                                                        width: '100%',
                                                        maxWidth: '400px',
                                                    },
                                                }}
                                                error={
                                                    doValidate &&
                                                    backupUserOne === -1 &&
                                                    (backupUserTwo !== -1 ||
                                                        backupUserThree !== -1)
                                                }
                                                variant="standard"
                                            />
                                            <Selector
                                                label="On Call Backup Two"
                                                data={userList.filter(
                                                    (user) =>
                                                        user.id !==
                                                            backupUserOne &&
                                                        user.id !==
                                                            backupUserThree,
                                                )}
                                                searchable
                                                currentValue={backupUserTwo}
                                                onChange={(e) => {
                                                    setBackupUserTwo(
                                                        e.target
                                                            .value as number,
                                                    )
                                                }}
                                                getDisplayString={(user) =>
                                                    user.name
                                                }
                                                customStyle={{
                                                    formControl: {
                                                        width: '100%',
                                                        maxWidth: '400px',
                                                    },
                                                }}
                                                error={
                                                    doValidate &&
                                                    backupUserTwo === -1 &&
                                                    backupUserThree !== -1
                                                }
                                                variant="standard"
                                            />
                                            <Selector
                                                label="On Call Backup Three"
                                                data={userList.filter(
                                                    (user) =>
                                                        user.id !==
                                                            backupUserOne &&
                                                        user.id !==
                                                            backupUserTwo,
                                                )}
                                                searchable
                                                currentValue={backupUserThree}
                                                onChange={(e) => {
                                                    setBackupUserThree(
                                                        e.target
                                                            .value as number,
                                                    )
                                                }}
                                                getDisplayString={(user) =>
                                                    user.name
                                                }
                                                customStyle={{
                                                    formControl: {
                                                        width: '100%',
                                                        maxWidth: '400px',
                                                    },
                                                }}
                                                variant="standard"
                                            />
                                        </Container>
                                    </Container>
                                    <Container
                                        className={classes.settingCard}
                                        style={{
                                            flexDirection: 'column',
                                            padding: theme.spacing(2),
                                            border: `1px solid ${theme.palette.grey[300]}`,
                                            borderRadius: theme.spacing(1),
                                            backgroundColor:
                                                theme.palette.grey[50],
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontWeight: 600,
                                                fontSize: '18px',
                                                marginBottom: theme.spacing(2),
                                                color:
                                                    theme.palette.primary.main,
                                                borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                paddingBottom: theme.spacing(
                                                    0.5,
                                                ),
                                                display: 'inline-block',
                                                alignSelf: 'flex-start',
                                                width: 'fit-content',
                                            }}
                                        >
                                            Service Request Configuration
                                        </span>
                                        <Container
                                            style={{
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                padding: theme.spacing(1.5),
                                                backgroundColor: 'white',
                                                borderRadius: theme.spacing(
                                                    0.5,
                                                ),
                                                border: `1px solid ${theme.palette.grey[200]}`,
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={
                                                            allowServiceRequest
                                                        }
                                                        color="primary"
                                                        onChange={(e) => {
                                                            setAllowServiceRequest(
                                                                !allowServiceRequest,
                                                            )
                                                        }}
                                                    />
                                                }
                                                labelPlacement="end"
                                                label={
                                                    <span
                                                        style={{
                                                            fontWeight: 500,
                                                            fontSize: '16px',
                                                            color: allowServiceRequest
                                                                ? theme.palette
                                                                      .primary
                                                                      .main
                                                                : theme.palette
                                                                      .text
                                                                      .secondary,
                                                        }}
                                                    >
                                                        Allow Service Request
                                                    </span>
                                                }
                                                style={{ margin: 0 }}
                                            />
                                        </Container>
                                    </Container>
                                </div>
                            </div>

                            <Container
                                style={{
                                    marginTop: theme.spacing(4),
                                    padding: theme.spacing(2),
                                    border: `1px solid ${theme.palette.grey[200]}`,
                                    borderRadius: theme.spacing(1),
                                    backgroundColor: theme.palette.grey[50],
                                    alignItems: 'center',
                                }}
                            >
                                <Button
                                    variant="contained"
                                    size="large"
                                    style={{
                                        backgroundColor:
                                            theme.palette.primary.main,
                                        color: 'white',
                                        textTransform: 'none',
                                        cursor: 'pointer',
                                        padding: theme.spacing(1.5, 4),
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        borderRadius: theme.spacing(2),
                                        boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
                                        transition: 'all 0.2s ease-in-out',
                                        minWidth: '140px',
                                        height: '48px',
                                    }}
                                    onClick={() => {
                                        if (checkValid()) {
                                            onSave()
                                        }
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform =
                                            'translateY(-2px)'
                                        e.currentTarget.style.boxShadow = `0 6px 20px ${theme.palette.primary.main}60`
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform =
                                            'translateY(0)'
                                        e.currentTarget.style.boxShadow = `0 4px 12px ${theme.palette.primary.main}40`
                                    }}
                                >
                                    {loading ? (
                                        <LoopIcon
                                            className={classes.rotateIcon}
                                        />
                                    ) : (
                                        'Save Changes'
                                    )}
                                </Button>
                            </Container>
                        </Container>
                    </Container>
                )}

                {selectedTab === COMMUNITY_TAB &&
                    workspaceUser?.active_workspace && (
                        <Container
                            style={{
                                flex: 1,
                                flexDirection: 'column',
                                padding: theme.spacing(1),
                            }}
                        >
                            <CommunitySettings
                                apartmentId={workspaceUser.active_workspace.id}
                                onWebsiteUrlSaved={(websiteUrl) => {
                                    if (!workspaceUser?.active_workspace) return
                                    dispatch(
                                        setWorkSpaceUser({
                                            ...workspaceUser,
                                            active_workspace: {
                                                ...workspaceUser.active_workspace,
                                                url: websiteUrl,
                                            },
                                        }),
                                    )
                                }}
                                onProfilePictureSaved={(profilePicture) => {
                                    if (!workspaceUser?.active_workspace) return
                                    dispatch(
                                        setWorkSpaceUser({
                                            ...workspaceUser,
                                            active_workspace: {
                                                ...workspaceUser.active_workspace,
                                                profile_picture: profilePicture,
                                            },
                                        }),
                                    )
                                }}
                            />
                        </Container>
                    )}

                {selectedTab === VENDOR_HOURS_TAB && (
                    <InHouseVendorHours
                        vendorList={vendorList}
                        setVendorList={setVendorList}
                    />
                )}

                {selectedTab === SCHOOLS_SERVED_TAB &&
                    workspaceUser?.active_workspace && (
                        <Container
                            style={{
                                flex: 1,
                                flexDirection: 'column',
                                padding: theme.spacing(1),
                            }}
                        >
                            <SchoolsServedSettings
                                apartmentId={workspaceUser.active_workspace.id}
                            />
                        </Container>
                    )}

                {selectedTab === LEASE_TERMS_TAB &&
                    workspaceUser?.active_workspace && (
                        <Container
                            style={{
                                flex: 1,
                                flexDirection: 'column',
                                padding: theme.spacing(1),
                            }}
                        >
                            <LeaseTermsSection
                                apartmentId={workspaceUser.active_workspace.id}
                            />
                        </Container>
                    )}
            </div>
        </div>
    )
}

const DETAILS_TAB = 0
const COMMUNITY_TAB = 1
const VENDOR_HOURS_TAB = 2
const SCHOOLS_SERVED_TAB = 3
const LEASE_TERMS_TAB = 4
